import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    formatPhoneNumber,
    fullSingleLineAddress,
    getYearsSinceAsString,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { AddressDisplay } from "components/contact-display/address-display";
import {
    ContactValueIcon,
    ContactValueLabel,
    ContactValueRow,
    contactValueTextClasses,
} from "components/contact-display/display-row";
import { labeledContactPhones } from "components/details-page-v-next/entity-contact-details";
import * as React from "react";

export type SharedContact = inferProcedureOutput<
    AppRouter["sharedPipeline"]["lead"]
>["selfContact"];

type ContactDetailKind = "phone" | "email" | "address";

interface ContactDetail {
    kind: ContactDetailKind;
    label: string;
    value: string;
}

export const present = (value: string | null | undefined): string | null =>
    value && value.trim().length > 0 ? value : null;

export const humanDate = (value: string | null): string | null =>
    value
        ? CalendarDate.parse(value)
              .map(date => date.humanFriendly())
              .getOrElse(value)
        : null;

export const approximateAge = (birthday: string): string | null => {
    const years = CalendarDate.parse(birthday)
        .map(date => getYearsSinceAsString(date))
        .getOrElse("");
    return years ? `About ${years}` : null;
};

export const formattedPhone = (value: string | null): string | null => {
    const shown = present(value);
    return shown ? formatPhoneNumber(shown) || shown : null;
};

const detail = (
    kind: ContactDetailKind,
    label: string,
    value: string | null | undefined,
): ContactDetail | null => {
    const shown = present(value);
    return shown ? { kind, label, value: shown } : null;
};

const phoneDetails = (contact: SharedContact): ContactDetail[] =>
    labeledContactPhones({ cell: contact.cellPhone, alternate: contact.phone1 }).map(
        phone => ({ kind: "phone", label: phone.label ?? "", value: phone.value }),
    );

const contactDetails = (contact: SharedContact): ContactDetail[] =>
    [
        ...phoneDetails(contact),
        detail("email", "Email", contact.email1),
        detail("address", "Address", fullSingleLineAddress(contact)),
    ].filter((row): row is ContactDetail => !!row);

export const hasSharedContactDetails = (contact: SharedContact): boolean =>
    contactDetails(contact).length > 0;

const PlainContactValue: React.FC<{
    Icon: ContactValueIcon;
    value: string;
    label: string | null;
}> = ({ Icon, value, label }) => (
    <ContactValueRow Icon={Icon}>
        <span className={contactValueTextClasses}>{value}</span>
        <ContactValueLabel label={label} />
    </ContactValueRow>
);

const ContactDetailValue: React.FC<{ detail: ContactDetail }> = ({ detail }) => {
    switch (detail.kind) {
        case "phone":
            return (
                <PlainContactValue
                    Icon={PhoneIcon}
                    value={formatPhoneNumber(detail.value) || detail.value}
                    label={detail.label || null}
                />
            );
        case "email":
            return (
                <PlainContactValue
                    Icon={EnvelopeIcon}
                    value={detail.value}
                    label={null}
                />
            );
        case "address":
            return <AddressDisplay value={detail.value} />;
        default: {
            const exhaustiveCheck: never = detail.kind;
            throw new Error(`Unhandled contact detail: ${exhaustiveCheck}`);
        }
    }
};

export const SharedContactDetails: React.FC<{ contact: SharedContact }> = ({
    contact,
}) => {
    const details = contactDetails(contact);
    if (details.length === 0) {
        return null;
    }
    return (
        <div className="flex flex-col items-start gap-x-5 gap-y-1.5 text-sm sm:flex-row sm:flex-wrap sm:items-center">
            {details.map(row => (
                <ContactDetailValue key={`${row.kind}-${row.value}`} detail={row} />
            ))}
        </div>
    );
};

export const LabeledSharedContactDetails: React.FC<{
    label: string;
    contact: SharedContact;
}> = ({ label, contact }) => {
    if (!hasSharedContactDetails(contact)) {
        return null;
    }
    return (
        <div className="flex flex-col gap-x-3 gap-y-0.5 sm:flex-row sm:items-center">
            <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                {label}
            </span>
            <SharedContactDetails contact={contact} />
        </div>
    );
};
